/**
 * Shared file-listing helpers for the conversation files panel
 * (ConversationFilesPanel). Extracted from the former ExportModal FilesTab so
 * there is ONE implementation (bug-patterns #386).
 */
import api from '../services/api';
import { AttachmentInfo } from '../components/MessageList';
import { DISPLAY_TIMEZONE } from './datetime';
import type { IconName } from '../components/Icon';
import { isVideoFile } from '../constants/video';

export type FileTypeFilter = 'all' | 'images' | 'documents' | 'config' | 'other';

/** How a file can be previewed inside the panel. */
export type PreviewKind = 'html' | 'svg' | 'text' | 'image' | 'pdf' | 'office' | 'sheet' | 'video' | 'none';

/** Spreadsheets previewed as a JSON table (`/preview-table` routes; mirrors
 *  backend `sheet_preview.py::SHEET_PREVIEW_EXTS`). Checked BEFORE OFFICE_EXTS,
 *  so xlsx/xlsm are 'sheet' while xls/ods stay 'office'. */
export const SHEET_EXTS = new Set(['csv', 'tsv', 'xlsx', 'xlsm']);

/** Office documents previewed as a server-rendered PDF (`/preview-pdf` routes;
 *  mirrors backend `office_preview.py::OFFICE_PREVIEW_EXTS`). */
const OFFICE_EXTS = new Set(['docx', 'doc', 'pptx', 'ppt', 'xlsx', 'xls', 'odt', 'odp', 'ods']);

const TEXT_EXTS = new Set([
  'md', 'txt', 'csv', 'log', 'json', 'yaml', 'yml', 'toml', 'ini', 'conf', 'cfg',
  'js', 'jsx', 'ts', 'tsx', 'py', 'css', 'sql', 'sh', 'xml',
]);
const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);

/** Text previews above this size are download-only (files panel + TextFilePreview). */
export const MAX_TEXT_PREVIEW_BYTES = 2 * 1024 * 1024;

function hasPrefix(bytes: Uint8Array, prefix: number[]): boolean {
  return bytes.length >= prefix.length && prefix.every((b, i) => bytes[i] === b);
}

/**
 * Decode a text file's bytes for preview. ONE implementation for every text
 * load path (bug-patterns #386): BOM UTF-8 / UTF-16LE / UTF-16BE first, then
 * strict UTF-8, then GB18030 (a superset of GBK -- the usual encoding of
 * Windows-authored Chinese .txt/.csv, which `Blob.text()` renders as mojibake).
 */
export function decodeTextBytes(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  if (hasPrefix(bytes, [0xef, 0xbb, 0xbf])) return new TextDecoder('utf-8').decode(bytes.subarray(3));
  if (hasPrefix(bytes, [0xff, 0xfe])) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  if (hasPrefix(bytes, [0xfe, 0xff])) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    try {
      return new TextDecoder('gb18030').decode(bytes);
    } catch {
      // Runtime without a GB18030 decoder: lossy UTF-8 beats no preview at all.
      return new TextDecoder('utf-8').decode(bytes);
    }
  }
}

/** Lower-case extension without the dot ('' when none). */
export function fileExt(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot + 1).toLowerCase();
}

/** Format bytes into a human-readable string. */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/** Classify a MIME type / filename into a filter category. */
export function classifyFileType(mimeType: string, filename: string): FileTypeFilter {
  if (mimeType.startsWith('image/')) return 'images';
  const ext = fileExt(filename);
  const docExts = ['pdf', 'docx', 'doc', 'xlsx', 'xls', 'txt', 'csv', 'md', 'odt'];
  if (docExts.includes(ext)) return 'documents';
  const configExts = ['json', 'yaml', 'yml', 'toml', 'ini', 'conf', 'cfg'];
  if (configExts.includes(ext)) return 'config';
  return 'other';
}

/** Short type label for the grey typeBadge. */
export function fileTypeBadge(mimeType: string, filename: string): string {
  const ext = fileExt(filename).toUpperCase();
  if (mimeType.startsWith('image/')) return ext || 'IMG';
  return ext || 'FILE';
}

/** ISO date string -> "MM-DD HH:MM" in DISPLAY_TIMEZONE (timezone-standard). */
export function formatFileDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const datePart = d.toLocaleDateString('en-CA', {
    timeZone: DISPLAY_TIMEZONE,
    month: '2-digit',
    day: '2-digit',
  });
  const timePart = d.toLocaleTimeString('en-GB', {
    timeZone: DISPLAY_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

/** True when the server can also render this file as PDF (`/preview-pdf`):
 *  gates the sheet「版式」view and the xlsx table -> PDF fallback. */
export function isOfficeConvertible(filename: string): boolean {
  return OFFICE_EXTS.has(fileExt(filename));
}

/** Decide the in-panel preview renderer for a file. */
export function previewKindFor(filename: string, mimeType = ''): PreviewKind {
  const ext = fileExt(filename);
  if (ext === 'html' || ext === 'htm' || mimeType === 'text/html') return 'html';
  if (ext === 'svg' || mimeType === 'image/svg+xml') return 'svg';
  if (ext === 'pdf' || mimeType === 'application/pdf') return 'pdf';
  if (isVideoFile(filename, mimeType)) return 'video';
  if (SHEET_EXTS.has(ext)) return 'sheet';
  if (OFFICE_EXTS.has(ext)) return 'office';
  if (IMAGE_EXTS.has(ext) || mimeType.startsWith('image/')) return 'image';
  if (TEXT_EXTS.has(ext) || mimeType.startsWith('text/')) return 'text';
  return 'none';
}

/**
 * Desktop screen-recording GIF name. The pattern source is LITERALLY equal to
 * backend `core/screen_recording.py::REC_FILENAME_RE` -- change both together.
 */
export const SCREEN_RECORDING_NAME_RE = /^rec_\d{8}-\d{6}-[0-9a-f]{4}\.gif$/;

/** True for a desktop screen-recording GIF (`rec_YYYYMMDD-HHMMSS-xxxx.gif`). */
export function isScreenRecordingName(name: string): boolean {
  return SCREEN_RECORDING_NAME_RE.test(name);
}

/** Above this the metadata probe gives up and the video is let through. */
const VIDEO_PROBE_TIMEOUT_MS = 5000;

/**
 * Read a local video's duration (seconds) through `<video preload="metadata">`.
 * Resolves `null` when the browser cannot tell (unsupported codec, no metadata,
 * timeout) — callers must let such a file through; the backend re-checks.
 */
export function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    let url = '';
    try { url = URL.createObjectURL(file); } catch { resolve(null); return; }
    const video = document.createElement('video');
    let done = false;
    const finish = (value: number | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), VIDEO_PROBE_TIMEOUT_MS);
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => finish(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => finish(null);
    video.src = url;
  });
}

const IMAGE_MIME_BY_EXT: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp',
};

/**
 * Raster MIME for an image preview blob. Some routes (personal relay) serve
 * `application/octet-stream`, so the blob is re-typed before it reaches <img>.
 * Prefers the attachment's own image/* type; svg never qualifies (it has its
 * own sandboxed preview kind).
 */
export function imageMimeType(filename: string, mimeType = ''): string {
  if (mimeType.startsWith('image/') && mimeType !== 'image/svg+xml') return mimeType;
  return IMAGE_MIME_BY_EXT[fileExt(filename)] || 'image/png';
}

const ICON_BY_EXT: Record<string, IconName> = {
  pdf: 'file-pdf',
  doc: 'file-doc', docx: 'file-doc', odt: 'file-doc', rtf: 'file-doc', md: 'file-doc', txt: 'file-doc',
  xls: 'file-sheet', xlsx: 'file-sheet', ods: 'file-sheet', csv: 'file-sheet',
  ppt: 'file-slides', pptx: 'file-slides', odp: 'file-slides', key: 'file-slides',
  html: 'file-html', htm: 'file-html',
  zip: 'file-archive', rar: 'file-archive', '7z': 'file-archive', tar: 'file-archive',
  gz: 'file-archive', tgz: 'file-archive', bz2: 'file-archive', xz: 'file-archive',
  js: 'file-code', jsx: 'file-code', ts: 'file-code', tsx: 'file-code', py: 'file-code',
  css: 'file-code', sql: 'file-code', sh: 'file-code', json: 'file-code', yaml: 'file-code',
  yml: 'file-code', xml: 'file-code', java: 'file-code', go: 'file-code', toml: 'file-code',
};

/** Type icon for a file row; unknown types get the generic `files` icon. */
export function fileIconFor(name: string, mime?: string | null): IconName {
  const byExt = ICON_BY_EXT[fileExt(name)];
  if (byExt) return byExt;
  const m = mime || '';
  if (m.startsWith('image/') || IMAGE_EXTS.has(fileExt(name)) || fileExt(name) === 'svg') {
    return 'file-image';
  }
  if (m === 'application/pdf') return 'file-pdf';
  if (m === 'text/html') return 'file-html';
  return 'files';
}

/** File-list sort order of the files panel (persisted in `cfp.sortMode`). */
export type FileSortMode = 'modified' | 'name';
export const SORT_MODE_KEY = 'cfp.sortMode';
export const DEFAULT_SORT_MODE: FileSortMode = 'modified';

export function readSortMode(): FileSortMode {
  try {
    const v = window.localStorage.getItem(SORT_MODE_KEY);
    return v === 'name' || v === 'modified' ? v : DEFAULT_SORT_MODE;
  } catch { return DEFAULT_SORT_MODE; }
}

export function writeSortMode(mode: FileSortMode): void {
  try { window.localStorage.setItem(SORT_MODE_KEY, mode); } catch { /* storage disabled */ }
}

/** File-list display of the files panel / relay picker (persisted in `cfp.viewMode`):
 *  'name' = plain rows, 'thumb' = card grid with server thumbnails. */
export type FileViewMode = 'name' | 'thumb';
export const VIEW_MODE_KEY = 'cfp.viewMode';
export const DEFAULT_VIEW_MODE: FileViewMode = 'name';
/** Cards shown per group in thumbnail mode before 加载更多 (and per relay page). */
export const THUMB_PAGE_SIZE = 10;

export function readViewMode(): FileViewMode {
  try {
    const v = window.localStorage.getItem(VIEW_MODE_KEY);
    return v === 'name' || v === 'thumb' ? v : DEFAULT_VIEW_MODE;
  } catch { return DEFAULT_VIEW_MODE; }
}

export function writeViewMode(mode: FileViewMode): void {
  try { window.localStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* storage disabled */ }
}

/** True when the server can render a thumbnail (`/thumbnail`): raster images, PDF and
 *  Office documents. SVG / text / html / archives never request one (icon only). */
export function hasThumbnail(name: string, mime = ''): boolean {
  const kind = previewKindFor(name, mime || '');
  if (kind === 'image' || kind === 'pdf' || kind === 'office') return true;
  // xlsx/xlsm resolve to 'sheet' first but are still Office documents.
  return kind === 'sheet' && isOfficeConvertible(name);
}

function dateMs(value?: string | null): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

const nameCollator = (a: string, b: string) => a.localeCompare(b, 'zh-CN', { numeric: true });

/**
 * Non-mutating sort: directories first; 'modified' = newest first with
 * undated items last (tie-break by name); 'name' = zh-CN numeric collation.
 */
export function sortFiles<T extends { name: string; modified?: string | null; isDir?: boolean }>(
  items: T[], mode: FileSortMode,
): T[] {
  return [...items].sort((a, b) => {
    if (!!a.isDir !== !!b.isDir) return a.isDir ? -1 : 1;
    if (mode === 'modified') {
      const ta = dateMs(a.modified);
      const tb = dateMs(b.modified);
      if (ta !== tb) {
        if (ta === null) return 1;
        if (tb === null) return -1;
        return tb - ta;
      }
    }
    return nameCollator(a.name, b.name);
  });
}

export interface AttachmentGroups {
  /** role !== 'user' (assistant / system output files). */
  agent: AttachmentInfo[];
  /** role === 'user'. */
  uploaded: AttachmentInfo[];
}

/** Split attachments by owning message role, each newest first. */
export function groupAttachments(items: AttachmentInfo[]): AttachmentGroups {
  const byNewest = [...items].sort((a, b) => {
    if (!a.created_at && !b.created_at) return 0;
    if (!a.created_at) return 1;
    if (!b.created_at) return -1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
  return {
    agent: byNewest.filter((a) => a.role !== 'user'),
    uploaded: byNewest.filter((a) => a.role === 'user'),
  };
}

/** Click a temporary <a download> for an object URL, then revoke it. */
export function triggerObjectUrlDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

/** Download a Blob under `filename`. */
export function downloadBlob(blob: Blob, filename: string): void {
  triggerObjectUrlDownload(URL.createObjectURL(blob), filename);
}

/**
 * Download a conversation attachment. iOS Safari ignores <a download>, so it
 * gets a pre-signed token URL in a new tab instead.
 */
export async function downloadAttachment(att: AttachmentInfo): Promise<void> {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) {
    const win = window.open('', '_blank');
    try {
      const tokenUrl = await api.fetchAttachmentDownloadToken(att.id);
      if (win) win.location.href = tokenUrl;
    } catch (err) {
      if (win) win.close();
      throw err;
    }
    return;
  }
  const blobUrl = await api.fetchAttachmentBlobUrl(att.id);
  triggerObjectUrlDownload(blobUrl, att.filename);
}
